import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ToastComponent } from './shared/components/ui/toast/toast.component';
import { SessionRevokedModalComponent } from './shared/components/modals/session-revoked-modal/session-revoked-modal.component';
import { AccountInactiveModalComponent } from './shared/components/modals/account-inactive-modal/account-inactive-modal.component';
import { TokenExpirationModalComponent } from './shared/components/modals/token-expiration-modal/token-expiration-modal.component';
import { AuthService } from './shared/services/auth.service';
import { TokenExpirationService } from './shared/services/token-expiration.service';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ToastComponent,
    SessionRevokedModalComponent,
    AccountInactiveModalComponent,
    TokenExpirationModalComponent
  ],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'AbuseXense';
  authService = inject(AuthService);
  tokenExpirationService = inject(TokenExpirationService);

  private authSubscription?: Subscription;
  private isRefreshing = false; // Prevent concurrent refresh requests

  /** Fallback warning duration (seconds) when modal state has no timeRemaining. From backend via login/refresh. */
  get defaultWarningDuration(): number {
    return this.tokenExpirationService.getWarningDurationSeconds();
  }

  modalState$ = this.tokenExpirationService.modalState$;

  ngOnInit(): void {
    // Start tracking if user is already authenticated
    if (this.authService.isAuthenticated()) {
      this.tokenExpirationService.startTracking();
    }

    // Start/stop tracking based on authentication status
    this.authSubscription = this.authService.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.tokenExpirationService.startTracking();
      } else {
        this.tokenExpirationService.stopTracking();
      }
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  onContinueSession(): void {
    // Prevent concurrent refresh requests
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;
    // CRITICAL: Stop countdown immediately to prevent race condition
    // If refresh is slow and timer hits 0, we don't want to trigger logout
    this.tokenExpirationService.stopCountdown();
    // Also stop background checks during refresh to prevent interference
    this.tokenExpirationService.stopTracking();

    this.authService.refreshToken().subscribe({
      next: () => {
        this.isRefreshing = false;
        this.tokenExpirationService.hideModal();
        // Notify other tabs that token was refreshed
        this.tokenExpirationService.notifyTokenRefreshed();
        // Resume tracking with new expiry time (60-second check resumes)
        this.tokenExpirationService.startTracking();
      },
      error: (err) => {
        this.isRefreshing = false;
        // Error handling is done in refreshToken method (auto-logout)
        // Tracking already stopped, so no race condition
      }
    });
  }

  onLogoutNow(): void {
    this.tokenExpirationService.stopTracking();
    this.authService.logout();
  }
}